import {
  ChatsCircle as ChatIcon,
  Circle as CheckboxOff,
  CheckCircle as CheckboxOn,
  Chat as CommentIcon,
  CheckFat as TaskIcon,
} from '@phosphor-icons/react';
import classNames from 'classnames';
import { AnimatePresence, m } from 'motion/react';
import React, { type FC, Fragment, type ReactElement, useContext, useRef, useState } from 'react';

import Buttons from '@/components/Buttons/Buttons';
import CircularProgress from '@/components/CircularProgress';
import CustomLink from '@/components/CustomLink';
import ChatCircle from '@/components/Icons/ChatCircle';
import CheckCircle from '@/components/Icons/CheckCircle';
import Link from '@/components/Icons/Link';
import Noodle from '@/components/Icons/Noodle';
import NoodleAI from '@/components/Icons/NoodleAI';
import X from '@/components/Icons/X';
import ProgressIndicator from '@/components/ProgressIndicator';
import UserImage from '@/components/UserImage';
import { useIsTextTruncated } from '@/hooks';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';

import Badge from '../Badge';
import SecondaryActions from '../SecondaryActions';

import s from './ListCell.module.scss';

interface ListCellProps {
  topLine?: string | null;
  id: string;
  label: string;
  contextText?: string | null;
  bottomLine?: string | ReactElement | null;
  aiMessage?: string | null;
  avatar?: {
    url?: string;
    name: string;
    color?: string;
  };
  needsAttention?: boolean;
  isRouting?: boolean;
  selectedId?: string;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onCheckboxChange?: (id: string, shiftKey: boolean) => void;
  isMuted?: boolean;
  isAi?: boolean;
  time?: string;
  progress?: number;
  messageCount?: number;
  href?: string;
  onClick?: () => void;
  badge?: Omit<Parameters<typeof Badge>[0], 'children'>;
  tag?: 'li' | 'div';
  isCard?: boolean;
  secondaryActions?: Parameters<typeof SecondaryActions>[0];
  inboxType?: 'task' | 'comment' | 'chat' | 'noodle-broadcast' | 'notification';
  linkedCells?: {
    type: 'workflows';
    items: Array<ListCellProps & { id: string }>;
  };
  labelComponents?: {
    component: React.ReactNode;
    key: string;
  }[];
  additionalComponents?: {
    component: React.ReactNode;
    key: string;
  }[];
}

const ListCell: FC<ListCellProps> = ({
  topLine,
  label,
  bottomLine,
  aiMessage,
  avatar,
  href,
  onClick,
  needsAttention,
  isRouting,
  id,
  selectedId,
  showCheckbox,
  isSelected,
  onCheckboxChange,
  isCard,
  isMuted,
  isAi,
  time,
  progress,
  messageCount,
  badge,
  tag,
  secondaryActions,
  inboxType,
  linkedCells,
  labelComponents,
  additionalComponents = [],
}) => {
  const { isInDashboard } = useContext(DashboardContext);
  const [showLinkedCells, setShowLinkedCells] = useState(!isInDashboard);

  const topLineRef = useRef(null);
  const isTopLineTruncated = useIsTextTruncated(topLineRef);

  const labelRef = useRef(null);
  const isLabelTruncated = useIsTextTruncated(labelRef);

  const bottomLineRef = useRef(null);
  const isBottomLineTruncated = useIsTextTruncated(bottomLineRef);

  const getInboxTypeIcon = (): ReactElement | null => {
    switch (inboxType) {
      case 'task':
        return <TaskIcon color='var(--color-warning)' size={12} className={s.inboxIcon} />;
      case 'comment':
        return <CommentIcon color='#8F00FF' size={12} className={s.inboxIcon} />;
      case 'chat':
        return <ChatIcon color='#C0A309' size={12} className={s.inboxIcon} />;
      case 'noodle-broadcast':
        return <Noodle width={12} height={12} />;
      case 'notification':
        return <ChatIcon color='#C0A309' size={12} className={s.inboxIcon} />;
      default:
        return null;
    }
  };

  const renderData = (): ReactElement => (
    <div
      className={classNames(s.container, isCard && s.cardData)}
      {...((selectedId === id || isMuted || linkedCells || isAi) && {
        style: {
          ...(selectedId === id &&
            !isCard && { backgroundColor: 'var(--color-gray-15)', boxShadow: 'inset 2px 0 0 var(--color-primary)' }),
          ...(isMuted && !inboxType && { opacity: 0.5 }),
          ...(linkedCells && linkedCells.items.length > 0 && { borderBottom: '1px dashed var(--color-gray-15)' }),
        },
      })}
    >
      <AnimatePresence>
        {inboxType && !isMuted ? <m.div className={s.unread} exit={{ scale: 0 }} /> : ''}
      </AnimatePresence>
      {avatar && <UserImage {...avatar} size={32} />}

      <div
        className={s.content}
        {...(secondaryActions && {
          style: {
            paddingInlineEnd: showCheckbox && onCheckboxChange ? '48px' : '24px',
          },
        })}
      >
        {(topLine || badge || inboxType) && (
          <small
            ref={topLineRef}
            title={isTopLineTruncated ? (topLine ?? undefined) : undefined}
            {...(inboxType === 'noodle-broadcast' && {
              style: {
                color: 'var(--color-noodle)',
                fontWeight: 'bold',
              },
            })}
          >
            {getInboxTypeIcon()} {badge && <Badge {...badge} />} {topLine}
          </small>
        )}
        <p
          ref={labelRef}
          title={isLabelTruncated ? label : undefined}
          style={{
            display: 'flex',
            gap: '6px',
          }}
          data-translate='false'
        >
          <span
            {...(selectedId === id && {
              style: {
                fontWeight: 'bold',
              },
            })}
          >
            {label}
          </span>
          {labelComponents
            ?.filter((labelComponent) => labelComponent.component)
            .map(({ component, key }) => (
              <Fragment key={key}>{component}</Fragment>
            ))}
        </p>

        {(bottomLine || linkedCells || aiMessage) &&
          (typeof bottomLine === 'string' ? (
            <small
              ref={bottomLineRef}
              title={isBottomLineTruncated && typeof bottomLine === 'string' ? bottomLine : undefined}
              {...(aiMessage && {
                style: {
                  alignItems: 'center',
                  display: 'flex',
                  gap: 'var(--spacer-xs)',
                },
              })}
            >
              {aiMessage && <NoodleAI size={12} />}
              {aiMessage || bottomLine || ''}
            </small>
          ) : (
            bottomLine
          ))}
      </div>
      {(messageCount && messageCount > 0 && (
        <div className={s.discussion}>
          <ChatCircle size={12} /> <span>{Intl.NumberFormat('en', { notation: 'compact' }).format(messageCount)}</span>
        </div>
      )) || <></>}
      {isRouting && <ProgressIndicator />}
      {(needsAttention || progress !== undefined || isAi) && !isRouting && !secondaryActions && (
        <div className={s.attention}>
          {needsAttention && <m.div initial={{ scale: 0 }} animate={{ scale: 1 }} />}
          {progress !== undefined && (
            <>
              {progress === 100 ? (
                <CheckCircle size={20} color='var(--color-success)' weight='fill' />
              ) : (
                <CircularProgress progress={progress} size={20} />
              )}
            </>
          )}
        </div>
      )}
      {isAi && !secondaryActions && !needsAttention && !isRouting && (
        <div className={s.ai}>
          <NoodleAI size={24} />
        </div>
      )}
      {showCheckbox && !onCheckboxChange && (
        <>
          {selectedId === id ? (
            <CheckboxOn weight='fill' size={24} color='var(--color-primary)' />
          ) : (
            <CheckboxOff size={24} color='var(--color-gray-100)' />
          )}
        </>
      )}
      {time && <div className={s.time}>{time}</div>}
    </div>
  );

  const renderLinkedCells = (): ReactElement[] | undefined =>
    linkedCells?.items.map((cellProps) => <ListCell key={cellProps.id} {...cellProps} selectedId={selectedId} />);

  const Tag = tag || 'li';

  return (
    <>
      <Tag
        className={classNames(s.item, isCard && s.card, onCheckboxChange && 'select-none')}
        {...(selectedId === id &&
          isCard && { style: { borderColor: 'transparent', boxShadow: '0 0 0 2px var(--color-primary)' } })}
      >
        {href && (
          <CustomLink to={href} onClick={onClick} prefetch={false}>
            {renderData()}
          </CustomLink>
        )}
        {!href && onClick && (
          <Buttons isWrapper isFullWidth onClick={onClick}>
            {renderData()}
          </Buttons>
        )}
        {!href && !onClick && renderData()}
        {secondaryActions && progress === undefined && !messageCount && !isRouting && (
          <div className={s.actionsWrapper}>
            {showCheckbox && onCheckboxChange ? (
              <div role='toolbar' className='flex items-center gap-1'>
                <SecondaryActions {...secondaryActions} position='bottom-right' />
                <button
                  aria-label={(isSelected ?? selectedId === id) ? 'Deselect item' : 'Select item'}
                  className='flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0 outline-none ring-0 shadow-none [&:focus-visible]:outline-none [&:focus-visible]:ring-0'
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onCheckboxChange(id, event.shiftKey);
                  }}
                  type='button'
                >
                  {(isSelected ?? selectedId === id) ? (
                    <CheckboxOn weight='fill' size={20} color='var(--color-primary)' />
                  ) : (
                    <CheckboxOff size={20} color='var(--color-gray-100)' />
                  )}
                </button>
              </div>
            ) : (
              <SecondaryActions {...secondaryActions} position='bottom-right' />
            )}
          </div>
        )}
        {linkedCells && linkedCells.items.length > 0 && (
          <Buttons
            style={{
              ...(selectedId === id && { backgroundColor: 'var(--color-gray-15)' }),
              ...(isMuted && { opacity: 0.5 }),
              ...(showLinkedCells && { backgroundColor: 'var(--color-gray-15)' }),
            }}
            className={s.linkedCellCta}
            isWrapper
            onClick={() => {
              setShowLinkedCells(!showLinkedCells);
            }}
          >
            {showLinkedCells ? <X color='var(--color-gray-100)' /> : <Link color='var(--color-gray-100)' />}
            {linkedCells.items.length === 1 ? (
              <>{isInDashboard ? linkedCells.items[0].bottomLine : linkedCells.items[0].label}</>
            ) : (
              <>
                {linkedCells.items.length} linked {linkedCells.type}
              </>
            )}
          </Buttons>
        )}
        {showLinkedCells && linkedCells && linkedCells.items.length > 0 && (
          <m.ul
            layout
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className={s.linkedCells}
          >
            {renderLinkedCells()}
          </m.ul>
        )}
      </Tag>
      {additionalComponents.map(({ component, key }) => (
        <Fragment key={key}>{component}</Fragment>
      ))}
    </>
  );
};

export default ListCell;
